/* eslint-disable react/jsx-handler-names */
import PropType from "prop-types";
import CustomImage from "../../../../atoms/customImage";
import IconButtonComponent from "../../../../atoms/iconButton";
import { desktopIcons } from "../../../../../constants/common/headerToolbarConstants";
import { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { CART, COUNTRIES } from "../../../../../constants/common/common";
import HeaderDropDownList from "./headerDropDownList";
import useAnalytics from "../../../../../analytics/useAnalytics";
import { cleverTapCommonEvents, reminderEvents } from "../../../../../analytics/clevertapConstants";
import { usePathname } from "next/navigation";
import { fetchSameDayCatalog, pageName } from "../../../../../utils/common";
import isEmpty from "lodash.isempty";
import { getPageTypeFromURL } from "../../../../../utils/fnpPageType";
import { ROUTE_CONSTANTS } from "../../../../../constants/route";

/**
 * DesktopToolBar component renders the toolbar for the desktop view.
 * Handles tab navigation, click events, and dropdowns for active tabs.
 *
 * @component
 * @returns {JSX.Element} The rendered toolbar component.
 */
const DesktopToolBar = ({ cartCount, serverSelectedCurrency, catalogId }) => {
  const { trackEvent } = useAnalytics();
  const iconRefs = useRef([]);
  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState("");
  const [sameDay, setSameDay] = useState([]);

  const loginDetail = useSelector((state) => state?.fus?.loginDetail);
  const selectedCurrencyFromState = useSelector((state) => state?.currencyList?.userSelectedCurrency);
  const { product, currencyFlagIcon } = useSelector(
    (state) => ({
      product: state?.pdp?.clientCurrentClickedItem,
      currencyFlagIcon: state?.currencyList?.currencyFlagIcon || [],
    }),
    shallowEqual,
  );

  /** */
  const fetchSameDayData = async () => {
    const res = await fetchSameDayCatalog();
    setSameDay(res);
  };

  useEffect(() => {
    if (catalogId !== COUNTRIES.INDIA) {
      fetchSameDayData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const urlIdentifier = product?.urlIdentifier;
  const [selectedCurrency, setSelectedCurrency] = useState(
    serverSelectedCurrency?.currency?.id || selectedCurrencyFromState?.currency?.id,
  );
  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");

  const commonData = {
    page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
  };

  useEffect(() => {
    if (selectedCurrencyFromState?.currency?.id && selectedCurrencyFromState?.currency?.id !== selectedCurrency) {
      setSelectedCurrency(selectedCurrencyFromState?.currency?.id);
    }
  }, [selectedCurrencyFromState, selectedCurrency]);

  /**
   * Handles clicks outside of any icons in the toolbar.
   * If the click is outside, it triggers onTabClick with null.
   *
   * @function handleClickOutside
   * @param {MouseEvent} event - The click event.
   */
  const handleClickOutside = useCallback((event) => {
    const clickedRef = iconRefs.current.find((ref) => ref && ref.contains(event.target));
    if (!clickedRef) {
      setActiveTab("");
    }
  }, []);

  /**
   * Adds event listener for mouse clicks outside the component when it mounts
   * and removes it when the component unmounts.
   */
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  /** */
  const setLabel = (label, id) => {
    if (id === "account") {
      if (loginDetail?.loggedin) {
        return loginDetail?.fullname;
      }
      return label;
    }
    if (selectedCurrency && id === "currency") {
      return selectedCurrency;
    }
    return label;
  };

  /**
   * Returns currency icon if id is "currency", otherwise the fallback URL.
   */
  const setIconSrc = (url, id) => {
    if (currencyFlagIcon.length && selectedCurrency && id === "currency") {
      return currencyFlagIcon?.find((item) => item?.countryCurrency === selectedCurrency)?.currencyIcon;
    }
    return url;
  };

  /**
   * Handles the click event for "same-day" items based on specific conditions.
   * Otherwise, it returns the item's landing URL as is.
   */
  const sameDayClickHandler = (item) => {
    if (item?.id === "sameDay" && !isEmpty(catalogId) && catalogId !== COUNTRIES.INDIA) {
      return `/${catalogId}${item?.landingUrl}`;
    }

    return item?.landingUrl;
  };

  /**
   *  * Handles header icon clicks, updates active tab, and triggers related actions/events.
   * - Prevents event bubbling.
   */
  const onHeaderIconClick = (item, event) => {
    event.stopPropagation();
    if (item?.label === CART) {
      window.location.href = `/${ROUTE_CONSTANTS.CHECKOUT}/${ROUTE_CONSTANTS.DELIVERY_ADDRESS}`;
      return;
    }
    const pageHeaderMenuData = {
      ...commonData,
      menu_name: item?.id,
    };
    if (activeTab !== item?.id) {
      trackEvent(cleverTapCommonEvents.headerMenuClicked, pageHeaderMenuData, ["clevertap"]);
    }
    setActiveTab(item?.id);
  };

  /** */
  const renderAnchorIcon = (item, index) => {
    return (
      <a
        href={sameDayClickHandler(item)}
        key={index}
        className="flex cursor-pointer items-center justify-center"
        tabIndex="0"
        id={item?.id}
        onClick={() => {
          const pageHeaderMenuData = {
            ...commonData,
            menu_name: item.label,
          };
          trackEvent(cleverTapCommonEvents.headerMenuClicked, pageHeaderMenuData, ["clevertap"]);
          if (item.id === "myReminders") {
            trackEvent(
              reminderEvents.REMINDER_PAGE_VIEWED,
              {
                page_name: pathname,
                page_type: getPageTypeFromURL(pathname),
                sources: "header toolbar",
                user_type: loginDetail?.userType,
              },
              ["clevertap"],
            );
          }
        }}
      >
        <div className="flex flex-col items-center justify-center gap-4">
          <CustomImage
            className="w-auto max-w-full"
            src={item?.url}
            width={25}
            height={24}
            isPriority={false}
            sizes="10vw"
            alt="desktop icons"
          />
          <div className="whitespace-nowrap text-center text-12 font-400 not-italic leading-4 text-grey-500 md:flex xxl:text-14">
            {item?.label}
          </div>
        </div>
      </a>
    );
  };

  /**
   * Renders an individual toolbar icon.
   *
   * @function renderIcons
   * @param {Object} item - The icon data including id, url, label, etc.
   * @param {number} index - The index of the current icon in the list.
   * @returns {JSX.Element} The rendered icon element.
   */
  const renderIcons = (item, index) => {
    /**
     * A callback function for assigning refs to icon elements.
     * This stores the DOM element reference for each icon in the `iconRefs` array.
     *
     * @function
     * @param {HTMLElement} el - The DOM element of the icon being referenced.
     */
    const ref = (el) => {
      iconRefs.current[index] = el;
    };
    let content;
    if (item.landingUrl && item?.label !== CART) {
      content =
        item.id !== "sameDay" || catalogId === COUNTRIES.INDIA || !!(sameDay?.length && sameDay?.includes(catalogId))
          ? renderAnchorIcon(item, index)
          : null;
    } else {
      content = (
        <div
          key={index}
          className={`relative flex cursor-pointer items-center justify-center ${activeTab === item?.id ? "border-b-4 border-fnp" : "border-b-4 border-white"}`}
          tabIndex="0"
          id={item?.id}
          ref={ref}
        >
          {item?.label === CART && cartCount > 0 && (
            <span className="absolute right-0 top-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-notification-secondary to-notification-primary p-4 text-10 font-500 text-white-900">
              {cartCount}
            </span>
          )}
          <IconButtonComponent
            classes="flex flex-col justify-center items-center gap-4 py-10 w-full overflow-hidden text-ellipsis max-w-[85px]"
            imgWidth={item?.width}
            iconSrc={setIconSrc(item?.url, item?.id)}
            labelTextSize="text-12"
            labelColor="w-full text-grey-500 font-normal"
            label={setLabel(item?.label, item?.id)}
            onClick={(event) => onHeaderIconClick(item, event)}
          />
          {activeTab === item?.id && (
            <>
              <HeaderDropDownList
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
                commonData={commonData}
              />
            </>
          )}
        </div>
      );
    }
    return content;
  };
  return <>{desktopIcons?.map((item, index) => renderIcons(item, index))}</>;
};
DesktopToolBar.propTypes = {
  cartCount: PropType.number,
  serverSelectedCurrency: PropType.object,
  catalogId: PropType.string,
};
export default DesktopToolBar;
